/**
 * WALL FINISH SPLITS (2026-10-02, Matt: "I want to be able to split a wall's finish
 * horizontally along its plane. I can do it vertically by simply splitting the wall,
 * but horizontally is hard when we have walls modeled down to floor finishes"). The
 * pure read side: a face's split lines (types.ts FaceSplitEntry) cut it into BANDS,
 * each wearing a finish. A band's finish is a FACE finish — what the face shows
 * (wainscot tile, a stone base, paint above) — the wall's thickness stays the one its
 * layers give it (the painted cladding layer of wallCladding.ts, when there is one).
 *
 * null band material = "the face's own finish": the outer layer on that side.
 * Heights are relative to the wall's base (level elevation + base offset).
 */
import type { AssemblyRecord, FaceSplitEntry, MaterialRecord } from './types'
import { applyWallFinishes, claddingSpecOf, type WallSide } from './wallCladding'

export const FACE_BASE_KEY: Record<WallSide, 'faceBaseExterior' | 'faceBaseInterior'> = {
  exterior: 'faceBaseExterior', interior: 'faceBaseInterior',
}
/** BAND COLORS (2026-10-07, Matt: painting the siding band of a split wall didn't change it — the
 *  brush colored the face's own, hidden, finish): the bottom band's brushed color */
export const FACE_BASE_COLOR_KEY: Record<WallSide, 'faceBaseColorExterior' | 'faceBaseColorInterior'> = {
  exterior: 'faceBaseColorExterior', interior: 'faceBaseColorInterior',
}

export function faceSplitsOf(p: Record<string, unknown> | null | undefined): FaceSplitEntry[] {
  const v = p?.faceSplits
  if (!Array.isArray(v)) return []
  return (v as FaceSplitEntry[]).filter((s) => s && typeof s.id === 'string'
    && (s.side === 'exterior' || s.side === 'interior') && Number.isFinite(s.zFt))
}

export interface FaceBand {
  /** band range, feet above the wall base — the bottom band starts at −∞, the top ends at +∞ */
  z0: number
  z1: number
  /** the finish painted on this band, or null = the face's own (layer) finish */
  materialId: string | null
  /** the split line under this band (null = the bottom band) */
  belowSplitId: string | null
  /** the split line over this band (null = the top band) */
  aboveSplitId: string | null
  /** the color brushed on this band (a colors.ts ref), null = none — a band wearing the face's
   *  own finish (materialId null) takes the face's color instead */
  color?: string | null
}

/** the bands of one face, bottom to top; a face with no split lines is one band
 *  (materialId = its base-band paint — normally null) */
export function faceBands(p: Record<string, unknown> | null | undefined, side: WallSide): FaceBand[] {
  const splits = faceSplitsOf(p).filter((s) => s.side === side).sort((a, b) => a.zFt - b.zFt)
  const base = p?.[FACE_BASE_KEY[side]]
  const out: FaceBand[] = []
  let z = -Infinity
  let mat: string | null = typeof base === 'string' && base ? base : null
  const baseColor = p?.[FACE_BASE_COLOR_KEY[side]]
  let color: string | null = typeof baseColor === 'string' && baseColor ? baseColor : null
  let below: string | null = null
  for (const s of splits) {
    // FACADE LINES: a band left unpainted from below takes the line above's "paint below"
    const bMat = mat ?? (typeof s.below === 'string' && s.below ? s.below : null)
    const bCol = color ?? (typeof s.belowColor === 'string' && s.belowColor ? s.belowColor : null)
    out.push({ z0: z, z1: s.zFt, materialId: bMat, belowSplitId: below, aboveSplitId: s.id, color: bCol })
    z = s.zFt
    mat = typeof s.above === 'string' && s.above ? s.above : null
    color = typeof s.aboveColor === 'string' && s.aboveColor ? s.aboveColor : null
    below = s.id
  }
  out.push({ z0: z, z1: Infinity, materialId: mat, belowSplitId: below, aboveSplitId: null, color })
  return out
}

/** does this face carry any split lines or band paint? (geometry only does band work then) */
export function faceHasBands(p: Record<string, unknown> | null | undefined, side: WallSide): boolean {
  const bands = faceBands(p, side)
  return bands.length > 1 || bands[0].materialId != null
}

/** the band holding height `zRel` (feet above the wall base) */
export function faceBandAt(p: Record<string, unknown> | null | undefined, side: WallSide, zRel: number): FaceBand {
  const bands = faceBands(p, side)
  return bands.find((b) => zRel >= b.z0 && zRel < b.z1) || bands[bands.length - 1]
}

/** one band of a face whose real finish build-up is a different thickness from the
 *  face's own (feet above the wall base; `deltaFt` > 0 = proud of the face) */
export interface FaceBandStep { z0: number; z1: number; materialId: string | null; deltaFt: number }

/**
 * SPLIT-BAND THICKNESS (2026-10-02, Matt: "painting the finish on a split wall should
 * allow each portion of the wall to be its appropriate thickness"). For each banded
 * face, every band's thickness CHANGE against the face as the wall is resolved: a band
 * painted a finish material (stone base under stucco, tile wainscot over gyp) builds
 * that finish by the same replace-or-add rule the whole-face paint uses
 * (`applyWallFinishes` on the type's own assembly, the other side held as painted);
 * the face's own band and paint-on bands are 0. Sides with no band stepping are
 * omitted; null when nothing steps anywhere.
 */
export function wallBandSteps(
  typeAssembly: AssemblyRecord,
  resolvedThicknessFt: number,
  p: Record<string, unknown>,
  getMat: (id: string) => MaterialRecord | null | undefined,
  findByName?: (name: string) => MaterialRecord | null | undefined,
): Partial<Record<WallSide, FaceBandStep[]>> | null {
  const thick = (a: AssemblyRecord) => a.layers.reduce((t, l) => t + l.thicknessFt, 0)
  const painted = (side: WallSide) => {
    const v = p[side === 'exterior' ? 'finishExterior' : 'finishInterior']
    return typeof v === 'string' && v ? v : null
  }
  const out: Partial<Record<WallSide, FaceBandStep[]>> = {}
  for (const side of ['exterior', 'interior'] as WallSide[]) {
    if (!faceHasBands(p, side)) continue
    const steps: FaceBandStep[] = []
    for (const b of faceBands(p, side)) {
      let deltaFt = 0
      if (b.materialId && b.materialId !== painted(side) && claddingSpecOf(getMat(b.materialId))) {
        const other: WallSide = side === 'exterior' ? 'interior' : 'exterior'
        const { assembly } = applyWallFinishes(typeAssembly, { [side]: b.materialId, [other]: painted(other) }, getMat, findByName)
        deltaFt = thick(assembly) - resolvedThicknessFt
      }
      steps.push({ z0: b.z0, z1: b.z1, materialId: b.materialId, deltaFt: Math.abs(deltaFt) < 1e-6 ? 0 : deltaFt })
    }
    if (steps.some((s) => s.deltaFt !== 0)) out[side] = steps
  }
  return out.exterior || out.interior ? out : null
}
